import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, Clock, ArrowRight, BookOpen, Bookmark } from "lucide-react";

const CAT_COLORS = {
  "Dependência Química": "bg-rose-100 text-rose-800 border-rose-200",
  "Saúde Mental": "bg-violet-100 text-violet-800 border-violet-200",
  "Tratamento": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Clínicas de Recuperação": "bg-sky-100 text-sky-800 border-sky-200",
  "Profissionais RAF": "bg-amber-100 text-amber-800 border-amber-200",
};

export default function PublicBlog() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(params.get("q") || "");
  const category = params.get("category") || "Todas";
  const page = Math.max(1, Number(params.get("page") || 1));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/public/blog", { params: { category: category !== "Todas" ? category : undefined, q: search || undefined, page } });
      setData(r.data);
    } finally { setLoading(false); }
  }, [category, search, page]);

  useEffect(() => { load(); }, [load]);

  const setCategory = (c) => { const p = new URLSearchParams(params); if (c === "Todas") p.delete("category"); else p.set("category", c); p.set("page", "1"); setParams(p); };
  const onSearch = (e) => { e.preventDefault(); const p = new URLSearchParams(params); if (search) p.set("q", search); else p.delete("q"); p.set("page", "1"); setParams(p); };
  const setPage = (n) => { const p = new URLSearchParams(params); p.set("page", String(n)); setParams(p); };

  const cats = ["Todas", ...(data?.categories || [])];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.per_page)) : 1;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-blog-page">
      <div className="bg-gradient-to-br from-[#0B2B62] via-[#0F3B8A] to-[#1E4FB8] text-white">
        <div className="max-w-6xl mx-auto px-4 py-16">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-[11px] tracking-[0.3em] uppercase opacity-70 flex items-center gap-2"><BookOpen className="w-3.5 h-3.5"/> Blog · RAF Direct</div>
            <Link to="/blog/salvos" data-testid="blog-saved-link" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold transition border border-white/20"><Bookmark className="w-3.5 h-3.5"/> Meus salvos</Link>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mt-3 leading-tight">Conteúdo que acolhe e orienta</h1>
          <p className="text-base sm:text-lg opacity-90 mt-4 max-w-2xl">Artigos escritos com cuidado sobre dependência química, saúde mental, tratamento e o papel dos profissionais na jornada da família.</p>
          <form onSubmit={onSearch} className="mt-8 flex gap-2 max-w-xl">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input type="search" value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar artigo..." data-testid="blog-search" className="w-full pl-10 pr-4 py-3 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-white/50"/>
            </div>
            <button type="submit" className="px-5 py-3 rounded-xl bg-white text-[#0B2B62] font-bold hover:bg-slate-100 transition" data-testid="blog-search-submit">Buscar</button>
          </form>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="blog-category-filters">
          {cats.map(c => (
            <button key={c} onClick={()=>setCategory(c)} data-testid={`blog-cat-${c.replace(/\s+/g,'-').toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${category===c ? "bg-[#0B2B62] text-white border-[#0B2B62]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
              {c}
              {data?.category_counts && c !== "Todas" && <span className="ml-1.5 text-[10px] opacity-60">({data.category_counts[c] || 0})</span>}
            </button>
          ))}
        </div>

        {loading && <div className="text-center py-16 text-slate-400 text-sm">Carregando artigos...</div>}
        {!loading && data?.posts?.length === 0 && (
          <div className="text-center py-20 text-slate-500">Nenhum artigo encontrado nessa busca.</div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
          {data?.posts?.map(p => {
            const fallback = `https://picsum.photos/seed/${p.id || p.slug}/1200/720`;
            return (
            <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
              className="group bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-slate-400 hover:shadow-lg transition flex flex-col">
              <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                <img src={p.hero_image || fallback} alt={p.title} loading="lazy"
                  onError={e=>{ if (e.target.dataset.fbk !== '1') { e.target.dataset.fbk = '1'; e.target.src = fallback; } }}
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-500"/>
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full border ${CAT_COLORS[p.category]||"bg-slate-100 text-slate-700 border-slate-200"}`}>{p.category}</span>
                  <span className="text-[11px] text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3"/>{p.read_minutes} min</span>
                </div>
                <h3 className="text-lg font-bold text-[#0B2B62] mb-2 leading-tight group-hover:text-[#1E4FB8] transition">{p.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-4 line-clamp-3 flex-1">{p.excerpt}</p>
                <span className="text-sm font-bold text-[#0B2B62] inline-flex items-center gap-1 group-hover:gap-2 transition-all">Saiba mais <ArrowRight className="w-4 h-4"/></span>
              </div>
            </Link>
          );})}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10" data-testid="blog-pagination">
            {Array.from({length: totalPages}, (_,i)=>i+1).map(n => (
              <button key={n} onClick={()=>setPage(n)} data-testid={`blog-page-${n}`}
                className={`w-9 h-9 rounded-lg text-sm font-bold ${n===page ? "bg-[#0B2B62] text-white" : "bg-white text-slate-700 border border-slate-200 hover:border-slate-400"}`}>{n}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
